import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TeamService } from "@/services/teamService";

// Query keys for teams
export const teamKeys = {
  all: ["teams"] as const,
  userTeams: (userId: string) => ["teams", "user", userId] as const,
  members: ["teamMembers"] as const,
  teamMembers: (teamId: string) => ["teamMembers", teamId] as const,
};

// Get all teams
export function useTeams() {
  return useQuery({
    queryKey: teamKeys.all,
    queryFn: async () => {
      try {
        const result = await TeamService.getAllTeams();
        return result;
      } catch (error) {
        console.error("❌ useTeams: Query failed", error);
        throw error;
      }
    },
    retry: 1,
    retryDelay: 1000,
  });
}

// Get user teams
export function useUserTeams(userId: string) {
  return useQuery({
    queryKey: teamKeys.userTeams(userId),
    queryFn: () => TeamService.getUserTeams(userId),
    enabled: !!userId,
  });
}

// Get all team members
export function useAllTeamMembers() {
  return useQuery({
    queryKey: teamKeys.members,
    queryFn: TeamService.getAllTeamMembers,
  });
}

// Get team members for specific team
export function useTeamMembers(teamId: string) {
  return useQuery({
    queryKey: teamKeys.teamMembers(teamId),
    queryFn: () => TeamService.getTeamMembers(teamId),
    enabled: !!teamId,
  });
}

// Create team
export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamData: { name: string; description?: string }) =>
      TeamService.createTeam(teamData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}

// Update team
export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      teamId,
      teamData,
    }: {
      teamId: string;
      teamData: { name: string; description?: string };
    }) => TeamService.updateTeam(teamId, teamData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}

// Delete team
export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamId: string) => TeamService.deleteTeam(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
      queryClient.invalidateQueries({ queryKey: teamKeys.members });
    },
  });
}

// Add team member
export function useAddTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      teamId,
      userId,
      role = "member",
    }: {
      teamId: string;
      userId: string;
      role?: "admin" | "member";
    }) => TeamService.addTeamMember(teamId, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members });
    },
  });
}

// Remove team member
export function useRemoveTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamMemberId: string) =>
      TeamService.removeTeamMember(teamMemberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members });
    },
  });
}
